/**
 * Dropdown component — Events and Methods
 *
 * Depends on: utility.js (Utility, BREAKPOINTS)
 * Must be loaded synchronously after utility.js and before global.js.
 *
 * HTML contract (do not change without updating JS):
 *   [is="drop-down"]          — root dropdown element
 *   .dropdown-toggle          — the trigger button inside the root
 *   .slideout-submenu         — optional back/close button inside the dropdown
 *   .dropdown.open            — open state class applied to the root
 *
 * @param {HTMLElement} element - Dropdown root element
 */
class Dropdown {
  static MQ_DESKTOP = window.matchMedia(`(min-width: ${BREAKPOINTS.lg}px)`);
  static MQ_IPAD_PRO_LANDSCAPE = window.matchMedia(`(min-width: ${BREAKPOINTS.ipadProMin}px) and (max-width: ${BREAKPOINTS.ipadProMax}px)`);

  static collapse(dropdown) {
    if (!dropdown) return;

    Utility.CSS.remove(dropdown, 'open');

    const toggle = dropdown.querySelector('.dropdown-toggle');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');

    dropdown.querySelectorAll('.dropdown.open').forEach(child => {
      Utility.CSS.remove(child, 'open');
      const childToggle = child.querySelector('.dropdown-toggle');
      if (childToggle) childToggle.setAttribute('aria-expanded', 'false');
    });
  }

  constructor(element) {
    this.dropdown = element;
    this.toggleCTA = (Utility?.DomSelector?.$('.dropdown-toggle', element)) || element.querySelector('.dropdown-toggle');
    this.supportedEvents = ["click", "touchstart", "touchmove", "touchend", "mouseenter", "mouseleave", "focusout"];
    this.touchState = { startX: 0, startY: 0, moved: false };

    if (!this.toggleCTA) {
      console.warn('Dropdown toggle element not found:', element);
      return;
    }

    this.init();
  }

  init() {
    const handlers = {
      click: (evt) => {
        evt.preventDefault();
        this.toggleDropdown();
      },
      touchstart: (evt) => {
        const touch = evt.touches?.[0];
        if (!touch) return;
        this.touchState.startX = touch.clientX;
        this.touchState.startY = touch.clientY;
        this.touchState.moved = false;
      },
      touchmove: (evt) => {
        const touch = evt.touches?.[0];
        if (!touch || this.touchState.moved) return;

        const deltaX = Math.abs(touch.clientX - this.touchState.startX);
        const deltaY = Math.abs(touch.clientY - this.touchState.startY);
        if (deltaX > 10 || deltaY > 10) {
          this.touchState.moved = true;
        }
      },
      touchend: (evt) => {
        if (this.touchState.moved) {
          this.touchState.moved = false;
          return;
        }
        evt.preventDefault();
        this.toggleDropdown();
        this.touchState.moved = false;
      },
      mouseenter: () => {
        if (!Dropdown.MQ_DESKTOP.matches) return;
        this.dropdownShow(this.dropdown);
        this.childOnFocusChange(this.dropdown);
      },
      mouseleave: () => {
        if (!Dropdown.MQ_DESKTOP.matches) return;
        this.dropdownHide(this.dropdown);
      },
      focusout: (evt) => {
        if (!Dropdown.MQ_DESKTOP.matches) return;
        if (!this.dropdown.contains(evt.relatedTarget) && this.dropdown.classList.contains('open')) {
          this.dropdownHide(this.dropdown);
        }
      }
    };

    this.supportedEvents.forEach((type) => {
      const target = (type === 'mouseleave' || type === 'focusout') ? this.dropdown : this.toggleCTA;
      const handler = handlers[type];
      if (handler) target.addEventListener(type, handler, { passive: type === 'touchmove' });
    });

    this.bindEvents();
  }

  bindEvents(){
    const closeBtn = Utility.DomSelector.$('.slideout-submenu', this.dropdown);
    if (closeBtn) {
      closeBtn.addEventListener('click', (evt) => {
        evt.preventDefault();
        this.dropdownToggle(this.dropdown);
      });
    }
  }

  /**
   * Toggle dropdown
   * @returns {Dropdown} Returns this for method chaining
   */
  toggleDropdown() {
    this.dropdownToggle(this.dropdown);
    return this;
  }

  /**
   * Toggle dropdown parent
   * @param {HTMLElement} dropdownParent
   * @returns {Dropdown} Returns this for method chaining
   */
  dropdownToggle(dropdownParent) {
    setTimeout(() => {
      dropdownParent.classList.contains('open')
        ? this.dropdownHide(dropdownParent)
        : this.dropdownShow(dropdownParent);
    }, 50);
    return this;
  }

  /**
   * Show dropdown
   * @param {HTMLElement} dropdownParent
   * @returns {Dropdown} Returns this for method chaining
   */
  dropdownShow(dropdownParent) {
    if (!dropdownParent) {
      return this;
    }

    if (Dropdown.MQ_IPAD_PRO_LANDSCAPE.matches && dropdownParent.parentElement) {
      dropdownParent.parentElement
        .querySelectorAll('[is="drop-down"].open')
        .forEach((openDropdown) => {
          if (openDropdown !== dropdownParent) Dropdown.collapse(openDropdown);
        });
    }

    this.toggleCTA.setAttribute('aria-expanded', true);
    Utility.CSS.add(dropdownParent, 'open');
    return this;
  }

  /**
   * Hide dropdown
   * @param {HTMLElement} dropdownParent
   * @returns {Dropdown} Returns this for method chaining
   */
  dropdownHide(dropdownParent) {
    if (!dropdownParent) return this;

    Dropdown.collapse(dropdownParent);

    return this;
  }

  childOnFocusChange(dropdownParent) {
    const openDropdowns = Array.from(dropdownParent.parentElement.querySelectorAll('.dropdown.open'));
    const currentIndex = openDropdowns.indexOf(dropdownParent);
    openDropdowns.forEach((ele) => {
      const eleIndex = openDropdowns.indexOf(ele);
      if (eleIndex !== currentIndex) {
        Dropdown.collapse(ele);
      }
    });
    return this;
  }
}

// Initialise all dropdown elements on the page
const allDropdowns = Utility.DomSelector.$$('[is="drop-down"]');
allDropdowns.forEach((ele) => {
  new Dropdown(ele);
});

//===============================================================================================================
//          Global Handler for Dropdowns and Custom Selects - Close all open on outside click                  //
//===============================================================================================================

(() => {
  /**
   * Closes all open custom-select dropdowns
   */
  const closeAllOpenSelects = () => {
    document.querySelectorAll('custom-select.open').forEach(select => {
      if (select.closeDropdown && typeof select.closeDropdown === 'function') {
        select.closeDropdown();
      }
    });
  };

  /**
   * Closes all open dropdown elements
   */
  const closeAllOpenDropdowns = () => {
    document.querySelectorAll('[is="drop-down"].open').forEach(dropdown => {
      Dropdown.collapse(dropdown);
    });
  };

  /**
   * Closes all open dropdown/select components
   */
  const closeAllOpenComponents = () => {
    closeAllOpenSelects();
    closeAllOpenDropdowns();
  };

  /**
   * Handles clicks/touches outside dropdown and custom-select elements
   * @param {Event} event - Click or touch event
   */
  const handleOutsideInteraction = (event) => {
    // Don't close if interaction is inside any dropdown or custom-select element
    if (event.target.closest('custom-select') || event.target.closest('[is="drop-down"]')) return;
    // Mobile nav accordions live in the drawer; ignore faux-"outside" clicks there so submenu
    // toggles behave (cart/filter drawers still use ordinary outside-click closing elsewhere).
    if (event.target.closest('.mobile-nav-drawer')) return;
    closeAllOpenComponents();
  };

  // Add global listeners for both click and touchstart (mobile support)
  document.body.addEventListener('click', handleOutsideInteraction);
  document.body.addEventListener('touchstart', handleOutsideInteraction, { passive: true });
})();

//===============================================================================================================
//          End of Global Handler for Dropdowns and Custom Selects                                              //
//===============================================================================================================
